import React, { useState, useEffect } from 'react';
import { SessionSummary, SessionCardAttempt, CardAttemptStats, Card, DeckProgress, Deck } from '../types';
import { getDatabaseService } from '../services/DatabaseFactory';

const Progress: React.FC = () => {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null);
  const [sessionCardAttempts, setSessionCardAttempts] = useState<SessionCardAttempt[]>([]);
  const [cardStats, setCardStats] = useState<{[cardId: string]: CardAttemptStats}>({});
  const [deckCards, setDeckCards] = useState<Card[]>([]);
  const [loadingDeckCards, setLoadingDeckCards] = useState(false);
  const [deckProgress, setDeckProgress] = useState<DeckProgress[]>([]);
  const [selectedDeck, setSelectedDeck] = useState<DeckProgress | null>(null);
  const [loadingDeckProgress, setLoadingDeckProgress] = useState(false);
  const [selectedDeckCards, setSelectedDeckCards] = useState<Card[]>([]);
  const [selectedDeckCardStats, setSelectedDeckCardStats] = useState<{[cardId: string]: CardAttemptStats}>({});
  const [loadingSelectedDeckCards, setLoadingSelectedDeckCards] = useState(false);
  const [activeTab, setActiveTab] = useState<'sessions' | 'decks'>('decks');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [loadingCardStats, setLoadingCardStats] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    loadSessions();
    loadDeckProgressData();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      const db = await getDatabaseService();
      const data = await db.getSessionsWithSummary();
      if (data && Array.isArray(data)) {
        setSessions(data);
      } else {
        setSessions([]);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError('Failed to load sessions');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionCardAttempts = async (sessionId: string) => {
    try {
      setLoadingAttempts(true);
      const db = await getDatabaseService();
      const attempts = await db.getSessionCardAttempts(sessionId);
      setSessionCardAttempts(attempts || []);
    } catch (err) {
      console.error('Error loading session card attempts:', err);
      setSessionCardAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const loadDeckCards = async (sessionId: string) => {
    try {
      setLoadingDeckCards(true);
      const db = await getDatabaseService();
      // Get the full session to extract deck IDs
      const sessions = await db.getSessions();
      const session = sessions.find(s => s.id === sessionId);
      if (session && session.deckIds.length > 0) {
        // Get all cards from the first deck (sessions typically use one deck)
        const cards = await db.getCards(session.deckIds[0]);
        setDeckCards(cards);
      } else {
        setDeckCards([]);
      }
    } catch (err) {
      console.error('Error loading deck cards:', err);
      setDeckCards([]);
    } finally {
      setLoadingDeckCards(false);
    }
  };

  const loadCardStats = async () => {
    try {
      setLoadingCardStats(true);
      const db = await getDatabaseService();
      const allStats: {[cardId: string]: CardAttemptStats} = {};
      // Load stats for all cards in the deck, not just attempted ones
      for (const card of deckCards) {
        const cardStats = await db.getCardAttemptStats(card.id);
        allStats[card.id] = cardStats;
      }
      setCardStats(allStats);
    } catch (err) {
      console.error('Error loading card stats:', err);
      setCardStats({});
    } finally {
      setLoadingCardStats(false);
    }
  };

  const handleSessionSelect = (session: SessionSummary) => {
    setSelectedSession(session);
    loadSessionCardAttempts(session.sessionId);
    loadDeckCards(session.sessionId);
  };

  // Load card stats when deck cards are loaded
  useEffect(() => {
    if (deckCards.length > 0) {
      loadCardStats();
    }
  }, [deckCards]);

  const loadDeckProgressData = async () => {
    try {
      setLoadingDeckProgress(true);
      const db = await getDatabaseService();
      const [sessionsData, decksData] = await Promise.all([
        db.getSessionsWithSummary(),
        db.getDecks()
      ]);

      // Group sessions by deck
      const deckSessionsMap = new Map<string, SessionSummary[]>();
      sessionsData.forEach(session => {
        const deckId = session.deckName; // Using deckName as identifier since sessions contain deck name
        if (!deckSessionsMap.has(deckId)) {
          deckSessionsMap.set(deckId, []);
        }
        deckSessionsMap.get(deckId)!.push(session);
      });

      // Calculate progress for each deck that has sessions
      const progressData: DeckProgress[] = Array.from(deckSessionsMap.entries()).map(([deckName, sessions]) => {
        const totalAttempts = sessions.reduce((sum, session) => sum + session.cardsAttempted, 0);
        const totalCorrect = sessions.reduce((sum, session) => sum + Math.round(session.cardsAttempted * session.percentageCorrect / 100), 0);
        const totalDuration = sessions.reduce((sum, session) => sum + (session.duration || 0), 0);
        
        const averageAccuracy = totalAttempts > 0 ? (totalCorrect / totalAttempts) * 100 : 0;
        const bestAccuracy = Math.max(...sessions.map(s => s.percentageCorrect));
        const recentSessions = sessions.slice(-3); // Last 3 sessions
        const recentAttempts = recentSessions.reduce((sum, session) => sum + session.cardsAttempted, 0);
        const recentCorrect = recentSessions.reduce((sum, session) => sum + Math.round(session.cardsAttempted * session.percentageCorrect / 100), 0);
        const recentAccuracy = recentAttempts > 0 ? (recentCorrect / recentAttempts) * 100 : 0;

        // Find deck ID by matching name
        const deck = decksData.find(d => d.name === deckName);
        
        return {
          deckId: deck?.id || `unknown_${deckName}`,
          deckName,
          totalSessions: sessions.length,
          totalAttempts,
          totalCorrect,
          averageAccuracy: Math.round(averageAccuracy * 100) / 100,
          bestAccuracy: Math.round(bestAccuracy * 100) / 100,
          recentAccuracy: Math.round(recentAccuracy * 100) / 100,
          totalDuration,
          lastStudied: sessions[sessions.length - 1]?.startTime,
          sessionsData: sessions.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
        };
      });

      // Sort by last studied (most recent first)
      progressData.sort((a, b) => {
        if (!a.lastStudied && !b.lastStudied) return 0;
        if (!a.lastStudied) return 1;
        if (!b.lastStudied) return -1;
        return new Date(b.lastStudied).getTime() - new Date(a.lastStudied).getTime();
      });

      setDeckProgress(progressData);
    } catch (err) {
      console.error('Error loading deck progress:', err);
      setDeckProgress([]);
    } finally {
      setLoadingDeckProgress(false);
    }
  };

  const loadSelectedDeckCardData = async (deckId: string) => {
    try {
      setLoadingSelectedDeckCards(true);
      const db = await getDatabaseService();
      // Load all cards for the deck
      const cards = await db.getCards(deckId);
      setSelectedDeckCards(cards);

      // Load stats for each card
      const cardStatsPromises = cards.map(card =>
        db.getCardAttemptStats(card.id)
      );
      const cardStatsResults = await Promise.all(cardStatsPromises);
      
      const statsMap: {[cardId: string]: CardAttemptStats} = {};
      cards.forEach((card, index) => {
        statsMap[card.id] = cardStatsResults[index];
      });
      
      setSelectedDeckCardStats(statsMap);
    } catch (err) {
      console.error('Error loading selected deck card data:', err);
      setSelectedDeckCards([]);
      setSelectedDeckCardStats({});
    } finally {
      setLoadingSelectedDeckCards(false);
    }
  };

  const handleClearProgress = async () => {
    try {
      setIsClearing(true);
      const db = await getDatabaseService();
      await db.clearProgressData();
      setSessions([]);
      setSelectedSession(null);
      setSessionCardAttempts([]);
      setCardStats({});
      setDeckProgress([]);
      setSelectedDeck(null);
      setSelectedDeckCards([]);
      setSelectedDeckCardStats({});
      setShowClearConfirmation(false);
    } catch (err) {
      console.error('Error clearing progress data:', err);
      setError('Failed to clear progress data');
    } finally {
      setIsClearing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'text-green-600 dark:text-green-400';
    if (accuracy >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getAllCardsWithStats = () => {
    // Get all cards from the deck, whether they were attempted or not
    const allCards = deckCards.map(card => ({
      cardId: card.id,
      cardName: card.name,
      stats: cardStats[card.id],
      wasAttempted: sessionCardAttempts.some(attempt => attempt.cardId === card.id)
    }));

    return allCards.sort((a, b) => a.cardName.localeCompare(b.cardName));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading progress data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Error Loading Progress</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadSessions}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 2rem)' }}>
      <div className="container mx-auto px-6 py-8 pb-12 flex flex-col h-full">
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Progress Tracking</h1>
              <p className="text-gray-600 dark:text-gray-400">
                View your study sessions and track your learning progress over time.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Tab Navigation */}
              <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('decks')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'decks'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Decks
                </button>
                <button
                  onClick={() => setActiveTab('sessions')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'sessions'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Sessions
                </button>
              </div>
              {sessions.length > 0 && (
                <button
                  onClick={() => setShowClearConfirmation(true)}
                  disabled={isClearing}
                  className="btn bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  title="Clear all progress data while preserving decks and cards"
                >
                  {isClearing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Clearing...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H7a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clear Progress Data
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          {activeTab === 'sessions' && sessions.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Progress Data</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Start studying with any deck to begin tracking your progress.
              </p>
            </div>
          ) : activeTab === 'sessions' ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
              {/* Sessions List */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col h-full min-h-0">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Study Sessions</h2>
                <div className="space-y-3 flex-1 overflow-y-auto">
                  {sessions.map((session) => (
                    <div
                      key={session.sessionId}
                      onClick={() => handleSessionSelect(session)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedSession?.sessionId === session.sessionId
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">{session.deckName}</h3>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(session.startTime)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          {session.cardsAttempted} attempts • {formatDuration(session.duration || 0)}
                        </span>
                        <span className={`font-medium ${getAccuracyColor(session.percentageCorrect)}`}>
                          {session.percentageCorrect.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Session Details */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col h-full min-h-0">
                {selectedSession ? (
                  <div className="h-full flex flex-col">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Session Details</h2>
                    
                    <div className="flex-1 overflow-y-scroll pr-2" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                      {/* Session Summary */}
                      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">{selectedSession.deckName}</h3>
                        <div className="grid grid-cols-2 gap-x-16 gap-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Date:</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatDate(selectedSession.startTime)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Duration:</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {formatDuration(selectedSession.duration || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Total Attempts:</span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {selectedSession.cardsAttempted}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">Accuracy:</span>
                            <span className={`font-medium ${getAccuracyColor(selectedSession.percentageCorrect)}`}>
                              {selectedSession.percentageCorrect.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Card Performance */}
                      <div className="mb-6">
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Card Performance</h3>
                        {loadingAttempts || loadingCardStats || loadingDeckCards ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading card data...</span>
                          </div>
                        ) : getAllCardsWithStats().length === 0 ? (
                          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                            No card data available for this session.
                          </p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {getAllCardsWithStats().map((card) => (
                              <div key={card.cardId} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div className="flex items-center">
                                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                      {card.cardName}
                                    </h4>
                                    {!card.wasAttempted && (
                                      <span className="ml-2 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded">
                                        Not attempted
                                      </span>
                                    )}
                                  </div>
                                  {card.stats && card.stats.totalAttempts > 0 ? (
                                    <div className="text-right text-xs">
                                      <div className={`font-medium ${getAccuracyColor(card.stats.percentageCorrect)}`}>
                                        {card.stats.percentageCorrect.toFixed(1)}%
                                      </div>
                                      <div className="text-gray-500 dark:text-gray-400">
                                        {card.stats.correctAttempts}/{card.stats.totalAttempts} attempts
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-right text-xs">
                                      <div className="text-gray-500 dark:text-gray-400">
                                        No attempts
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {card.stats && card.stats.totalAttempts > 0 && (
                                  <div className="mt-2 flex items-center">
                                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full ${
                                          card.stats.percentageCorrect >= 80 ? 'bg-green-500' :
                                          card.stats.percentageCorrect >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${card.stats.percentageCorrect}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Card Attempts */}
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Card Attempts</h3>
                        {loadingAttempts ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading attempts...</span>
                          </div>
                        ) : sessionCardAttempts.length === 0 ? (
                          <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                            No card attempts found for this session.
                          </p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {sessionCardAttempts.map((attempt, index) => (
                              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                      {attempt.cardName}
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {formatDate(attempt.timestamp)}
                                    </p>
                                  </div>
                                  <div className="flex items-center">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      attempt.isCorrect 
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                      {attempt.isCorrect ? 'Correct' : 'Incorrect'}
                                    </span>
                                  </div>
                                </div>
                                {attempt.userAnswer && (
                                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Answer:</span> {attempt.userAnswer}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a Session</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Choose a study session from the list to view detailed performance data.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Decks Tab */
            <div className="h-full">
              {loadingDeckProgress ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading deck progress...</span>
                </div>
              ) : deckProgress.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Deck Progress</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Start studying with any deck to begin tracking deck-level progress.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
                  {/* Deck Progress List */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col h-full min-h-0">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Deck Progress</h2>
                    <div className="space-y-3 flex-1 overflow-y-auto">
                      {deckProgress.map((deck) => (
                        <div
                          key={deck.deckId}
                          onClick={() => {
                            setSelectedDeck(deck);
                            loadSelectedDeckCardData(deck.deckId);
                          }}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedDeck?.deckId === deck.deckId
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="font-medium text-gray-900 dark:text-white">{deck.deckName}</h3>
                            <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0 ml-4">
                              {deck.lastStudied ? formatDate(deck.lastStudied) : 'Never studied'}
                            </span>
                          </div>
                          <div className="text-sm space-y-0.5">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Sessions: <span className="font-medium text-gray-900 dark:text-white">{deck.totalSessions}</span></span>
                              <span className="text-gray-600 dark:text-gray-400">Attempts: <span className="font-medium text-gray-900 dark:text-white">{deck.totalAttempts}</span></span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Avg Accuracy: <span className={`font-medium ${getAccuracyColor(deck.averageAccuracy)}`}>{deck.averageAccuracy.toFixed(1)}%</span></span>
                              <span className="text-gray-600 dark:text-gray-400">Best: <span className={`font-medium ${getAccuracyColor(deck.bestAccuracy)}`}>{deck.bestAccuracy.toFixed(1)}%</span></span>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center">
                            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${
                                  deck.averageAccuracy >= 80 ? 'bg-green-500' :
                                  deck.averageAccuracy >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${deck.averageAccuracy}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Deck Details */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col h-full min-h-0">
                    {selectedDeck ? (
                      <div className="h-full flex flex-col">
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Deck Details</h2>
                        
                        <div className="flex-1 overflow-y-scroll pr-2" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
                          {/* Deck Summary */}
                          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">{selectedDeck.deckName}</h3>
                            <div className="grid grid-cols-2 gap-x-16 gap-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Total Sessions:</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {selectedDeck.totalSessions}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Total Time:</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {formatDuration(selectedDeck.totalDuration)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Total Attempts:</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {selectedDeck.totalAttempts}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Average Accuracy:</span>
                                <span className={`font-medium ${getAccuracyColor(selectedDeck.averageAccuracy)}`}>
                                  {selectedDeck.averageAccuracy.toFixed(1)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Best Accuracy:</span>
                                <span className={`font-medium ${getAccuracyColor(selectedDeck.bestAccuracy)}`}>
                                  {selectedDeck.bestAccuracy.toFixed(1)}%
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500 dark:text-gray-400">Recent Accuracy:</span>
                                <span className={`font-medium ${getAccuracyColor(selectedDeck.recentAccuracy)}`}>
                                  {selectedDeck.recentAccuracy.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Cards Performance */}
                          <div className="mb-6">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Cards Performance</h3>
                            {loadingSelectedDeckCards ? (
                              <div className="flex items-center justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading cards...</span>
                              </div>
                            ) : selectedDeckCards.length === 0 ? (
                              <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                                No cards found for this deck.
                              </p>
                            ) : (
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {selectedDeckCards
                                  .sort((a, b) => {
                                    const aStats = selectedDeckCardStats[a.id];
                                    const bStats = selectedDeckCardStats[b.id];
                                    // Sort by total attempts (descending), then by accuracy (ascending for cards needing work)
                                    if (!aStats && !bStats) return a.name.localeCompare(b.name);
                                    if (!aStats) return 1;
                                    if (!bStats) return -1;
                                    if (aStats.totalAttempts !== bStats.totalAttempts) {
                                      return bStats.totalAttempts - aStats.totalAttempts;
                                    }
                                    return aStats.percentageCorrect - bStats.percentageCorrect;
                                  })
                                  .map((card) => {
                                    const stats = selectedDeckCardStats[card.id];
                                    return (
                                      <div key={card.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                              {card.name}
                                            </h4>
                                            {stats && stats.totalAttempts > 0 ? (
                                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {stats.correctAttempts}/{stats.totalAttempts} attempts
                                              </p>
                                            ) : (
                                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Never attempted
                                              </p>
                                            )}
                                          </div>
                                          <div className="text-right ml-4">
                                            {stats && stats.totalAttempts > 0 ? (
                                              <div className="text-xs">
                                                <div className={`font-medium ${getAccuracyColor(stats.percentageCorrect)}`}>
                                                  {stats.percentageCorrect.toFixed(1)}%
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                                No data
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        {stats && stats.totalAttempts > 0 && (
                                          <div className="mt-2 flex items-center">
                                            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                              <div
                                                className={`h-1.5 rounded-full ${
                                                  stats.percentageCorrect >= 80 ? 'bg-green-500' :
                                                  stats.percentageCorrect >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${stats.percentageCorrect}%` }}
                                              ></div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                              </div>
                            )}
                          </div>

                          {/* Recent Sessions for this Deck */}
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Recent Sessions</h3>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {selectedDeck.sessionsData.slice(0, 10).map((session) => (
                                <div key={session.sessionId} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                        {formatDate(session.startTime)}
                                      </h4>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        {session.cardsAttempted} attempts • {formatDuration(session.duration || 0)}
                                      </p>
                                    </div>
                                    <div className="text-right text-xs">
                                      <div className={`font-medium ${getAccuracyColor(session.percentageCorrect)}`}>
                                        {session.percentageCorrect.toFixed(1)}%
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Select a Deck</h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          Choose a deck from the list to view detailed progress and recent sessions.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Clear Confirmation Modal */}
        {showClearConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Clear Progress Data</h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This will permanently delete all study session data and progress statistics. Your decks and cards will not be affected. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowClearConfirmation(false)}
                  disabled={isClearing}
                  className="flex-1 btn bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearProgress}
                  disabled={isClearing}
                  className="flex-1 btn bg-red-600 hover:bg-red-700 text-white text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isClearing ? 'Clearing...' : 'Clear Progress Data'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Progress;